import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

import { API } from '../lib/ortam';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [kullanici, setKullanici] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('yks_token') || null);
  const [yukleniyor, setYukleniyor] = useState(true);

  // Token ile profil kontrol
  useEffect(() => {
    if (!token) { setYukleniyor(false); return; }

    fetch(`${API}/api/auth/profil`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) throw new Error('Token gecersiz');
        return r.json();
      })
      .then(data => {
        setKullanici(data.kullanici);
      })
      .catch(() => {
        localStorage.removeItem('yks_token');
        localStorage.removeItem('yks_refresh');
        setToken(null);
        setKullanici(null);
      })
      .finally(() => setYukleniyor(false));
  }, [token]);

  const girisYap = useCallback(async (email, sifre) => {
    const r = await fetch(`${API}/api/auth/giris`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, sifre }),
    });
    const data = await r.json();
    if (!data.basarili) throw new Error(data.hata);

    localStorage.setItem('yks_token', data.accessToken);
    localStorage.setItem('yks_refresh', data.refreshToken);
    setToken(data.accessToken);
    setKullanici(data.kullanici);
    return data;
  }, []);

  const cikisYap = useCallback(() => {
    localStorage.removeItem('yks_token');
    localStorage.removeItem('yks_refresh');
    setToken(null);
    setKullanici(null);
  }, []);

  // Authenticated fetch wrapper
  const authFetch = useCallback(async (url, options = {}) => {
    const headers = { ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (!headers['Content-Type'] && options.body) headers['Content-Type'] = 'application/json';

    const r = await fetch(url, { ...options, headers });

    // 401 — token expired, try refresh
    if (r.status === 401 && token) {
      const refreshToken = localStorage.getItem('yks_refresh');
      if (refreshToken) {
        try {
          const rr = await fetch(`${API}/api/auth/yenile`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });
          const rd = await rr.json();
          if (rd.basarili) {
            localStorage.setItem('yks_token', rd.accessToken);
            setToken(rd.accessToken);
            // Retry original request
            headers['Authorization'] = `Bearer ${rd.accessToken}`;
            return fetch(url, { ...options, headers });
          }
        } catch {}
      }
      cikisYap();
    }

    return r;
  }, [token, cikisYap]);

  const rolYeterli = useCallback((gerekliRol) => {
    if (!kullanici) return false;
    // musteri (2026-10-26) hiyerarşide 0: hiçbir rolYeterli kapısından geçmez (sunucu services/auth ile aynı).
    const seviyeler = { musteri: 0, ogretmen: 1, admin: 2, super_admin: 3 };
    return (seviyeler[kullanici.rol] || 0) >= (seviyeler[gerekliRol] || 0);
  }, [kullanici]);

  // Ders-bazli yetki (2026-06-21): izinli_dersler null/bos = KISITSIZ (tum dersler).
  // Dolu dizi = yalniz o ders'leri gorur/degerlendirir. Backend bunu zorlar; bu
  // sadece UI gizleme (dropdown daraltma) icin.
  const izinliDersler = (kullanici && Array.isArray(kullanici.izinli_dersler) && kullanici.izinli_dersler.length > 0)
    ? kullanici.izinli_dersler : null;
  const dersGorebilir = useCallback((ders) => {
    if (!kullanici) return false;
    const izin = kullanici.izinli_dersler;
    if (!Array.isArray(izin) || izin.length === 0) return true; // kisitsiz
    return izin.includes(ders);
  }, [kullanici]);

  return (
    <AuthContext.Provider value={{
      kullanici, token, yukleniyor,
      girisYap, cikisYap, authFetch, rolYeterli,
      izinliDersler, dersGorebilir
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
